import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'products', 'features', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="px-20 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/cfe25e78-c4d7-4170-8c24-70509b18ed91.png" alt="DENAC d.o.o." className="h-10 w-10 object-contain" />
            <span className={`text-lg font-light tracking-wider ${scrolled ? 'text-gray-900' : 'text-white'}`}>DENAC d.o.o.</span>
          </div>
          <div className="flex items-center gap-10">
            {['home', 'about', 'products', 'features', 'contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item)}
                className={`text-sm font-light tracking-wide capitalize transition-all whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'
                } ${activeSection === item ? (scrolled ? 'text-gray-900' : 'text-white') : ''}`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-800"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-0 w-1/2 h-full bg-gradient-to-bl from-purple-500/30 to-transparent blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-indigo-500/30 to-transparent blur-3xl"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        <div className="relative z-10 w-full px-20 py-32">
          <div className="max-w-7xl">
            <div className="space-y-4">
              <h1 className="text-8xl font-bold text-white leading-tight">Modern iOS</h1>
              <h1 className="text-9xl font-light italic text-white/95 ml-16 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>Applications</h1>
              <h1 className="text-8xl font-bold text-white leading-tight">For Enhanced</h1>
              <h1 className="text-8xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent leading-tight">Mobile Experience</h1>
            </div>
            
            <div className="mt-32 grid grid-cols-2 gap-16 max-w-3xl">
              <div>
                <p className="text-base text-gray-300 leading-relaxed">
                  Expert VPN solutions delivering unparalleled security and privacy protection for iOS users worldwide, ensuring safe browsing and data encryption.
                </p>
              </div>
              <div>
                <p className="text-base text-gray-300 leading-relaxed">
                  Advanced Cleaner applications optimizing device performance, freeing storage space, and maintaining peak efficiency for seamless mobile experiences.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-32 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-20">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-2 h-2 bg-orange-500 rotate-45"></div>
            <span className="text-xs uppercase tracking-widest text-orange-500 font-medium">WHO WE ARE</span>
          </div>
          
          <h2 className="text-7xl font-bold text-gray-900 leading-tight mb-12 max-w-4xl">
            Expert Team Building<br />
            <span className="ml-20">the Future of iOS</span>
          </h2>

          <p className="text-xl text-gray-700 leading-relaxed max-w-3xl">
            DENAC d.o.o. is a dedicated team of iOS development experts specializing in creating modern applications that enhance user experience and improve mobile device security. Our flagship products include advanced VPN solutions and intelligent Cleaner applications, designed to deliver exceptional performance and protection for iOS users worldwide.
          </p>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-20">
          <h2 className="text-6xl font-bold text-gray-900 text-center mb-24">Our Applications</h2>
          
          <div className="grid grid-cols-3 gap-12">
            <div className="group relative rounded-[28px] overflow-hidden h-[380px] cursor-pointer hover:scale-105 transition-transform duration-300">
              <img 
                src="https://public.readdy.ai/ai/img_res/ac42d48ad4772c6abe4a1910556717ea.jpg" 
                alt="VPN Application" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-10 text-center">
                <h3 className="text-3xl font-bold text-white mb-2">VPN Application</h3>
                <p className="text-base text-gray-300">Security & Privacy</p>
              </div>
            </div>

            <div className="group relative rounded-[28px] overflow-hidden h-[380px] cursor-pointer hover:scale-105 transition-transform duration-300">
              <img 
                src="https://readdy.ai/api/search-image?query=device-cleaner-optimization-interface-with-green-teal-gradient-background-displaying-storage-management-icons-performance-boost-elements-and-cleaning-tools-in-modern-ios-application-style-fresh-minimalist-design&width=380&height=380&seq=cleaner001&orientation=squarish" 
                alt="Cleaner Application" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-10 text-center">
                <h3 className="text-3xl font-bold text-white mb-2">Cleaner Application</h3>
                <p className="text-base text-gray-300">Performance & Storage</p>
              </div>
            </div>

            <div className="group relative rounded-[28px] overflow-hidden h-[380px] cursor-pointer hover:scale-105 transition-transform duration-300">
              <img 
                src="https://readdy.ai/api/search-image?query=abstract-purple-mesh-gradient-background-with-futuristic-ios-interface-elements-showing-upcoming-mobile-application-features-modern-tech-design-with-smooth-flowing-shapes-and-innovative-digital-aesthetic&width=380&height=380&seq=future001&orientation=squarish" 
                alt="Future Product" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-10 text-center">
                <h3 className="text-3xl font-bold text-white mb-2">Coming Soon</h3>
                <p className="text-base text-gray-300">Innovation in Progress</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-20">
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className="w-2 h-2 bg-blue-500 rotate-45"></div>
              <span className="text-xs uppercase tracking-widest text-gray-600 font-medium">WHAT WE DELIVER</span>
            </div>
            <h2 className="text-6xl font-bold text-gray-900">Comprehensive iOS Solutions</h2>
          </div>

          <div className="grid grid-cols-3 gap-8 mb-8">
            <div className="bg-blue-50 rounded-3xl p-10 h-[460px] flex flex-col">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-keyhole-line text-blue-600 text-2xl"></i>
              </div>
              <div className="text-7xl text-purple-300 opacity-30 mb-6">"</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Advanced VPN Technology</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Military-grade encryption protocols ensuring complete anonymity and protection across all networks, with lightning-fast connection speeds and global server coverage.
              </p>
            </div>

            <div className="bg-blue-50 rounded-3xl p-10 h-[460px] flex flex-col">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mb-6">
                <i className="ri-speed-up-line text-teal-600 text-2xl"></i>
              </div>
              <div className="text-7xl text-purple-300 opacity-30 mb-6">"</div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Intelligent Device Optimization</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Smart algorithms that analyze and optimize device performance, removing junk files, managing storage efficiently, and boosting overall system responsiveness.
              </p>
            </div>

            <div className="relative rounded-3xl overflow-hidden h-[460px]">
              <img 
                src="https://readdy.ai/api/search-image?query=purple-to-blue-gradient-abstract-ios-interface-design-showing-seamless-user-experience-with-smooth-app-navigation-elements-modern-mobile-ui-components-and-intuitive-design-patterns-in-elegant-minimalist-style&width=340&height=460&seq=ux001&orientation=portrait" 
                alt="User Experience" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
              <div className="absolute bottom-10 left-10">
                <h3 className="text-2xl font-bold text-white mb-2">Seamless User Experience</h3>
                <p className="text-base text-gray-200">Intuitive Design</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-2 relative rounded-3xl overflow-hidden h-[380px]">
              <img 
                src="https://readdy.ai/api/search-image?query=dark-blue-gradient-abstract-app-interface-background-showcasing-security-first-approach-with-encryption-elements-shield-icons-and-protection-layers-in-professional-ios-application-design-modern-tech-aesthetic&width=540&height=380&seq=security001&orientation=landscape" 
                alt="Security First" 
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/70 to-transparent"></div>
              <div className="absolute bottom-12 left-12 max-w-md">
                <h3 className="text-4xl font-bold text-white mb-2">Security First</h3>
                <p className="text-lg text-gray-200 mb-4">Approach</p>
                <p className="text-base text-gray-300 leading-relaxed">
                  Every feature is built with security at its core, implementing industry-leading standards and continuous monitoring to protect user data and privacy.
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="bg-blue-50 rounded-3xl p-8 h-[180px] flex flex-col justify-between">
                <div className="text-5xl text-purple-300 opacity-30">"</div>
                <div className="flex items-end justify-between">
                  <h3 className="text-xl font-bold text-gray-900">Privacy Protection</h3>
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <i className="ri-lock-line text-blue-600 text-lg"></i>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-3xl p-8 h-[180px] flex flex-col justify-between">
                <div className="text-5xl text-purple-300 opacity-30">"</div>
                <div className="flex items-end justify-between">
                  <h3 className="text-xl font-bold text-gray-900">Performance Enhancement</h3>
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                    <i className="ri-flashlight-line text-teal-600 text-lg"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-gradient-to-b from-gray-900 to-black py-32">
        <div className="max-w-7xl mx-auto px-20">
          <div className="flex items-center justify-between mb-32">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/cfe25e78-c4d7-4170-8c24-70509b18ed91.png" alt="DENAC d.o.o." className="h-8 w-8 object-contain" />
              <span className="text-2xl text-white font-serif">DENAC d.o.o.</span>
            </div>
            <p className="text-base text-gray-400">Transforming mobile experiences since 2016</p>
          </div>

          <div className="grid grid-cols-4 gap-16 mb-32">
            <div>
              <h4 className="text-lg text-white font-bold mb-6">Company</h4>
              <div className="space-y-6">
                {['Home', 'About', 'Products', 'Features'].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item.toLowerCase())}
                    className="block text-base text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg text-white font-bold mb-6">Solutions</h4>
              <div className="space-y-6">
                <p className="text-base text-gray-400">VPN Application</p>
                <p className="text-base text-gray-400">Cleaner Tool</p>
                <p className="text-base text-gray-400">iOS Development</p>
                <p className="text-base text-gray-400">Security Features</p>
              </div>
            </div>

            <div>
              <h4 className="text-lg text-white font-bold mb-6">Resources</h4>
              <div className="space-y-6">
                <p className="text-base text-gray-400">Documentation</p>
                <p className="text-base text-gray-400">Support</p>
                <p className="text-base text-gray-400">Privacy Policy</p>
                <p className="text-base text-gray-400">Terms of Service</p>
              </div>
            </div>

            <div>
              <p className="text-xl text-white font-serif mb-2">© DENAC d.o.o. 2025</p>
              <p className="text-sm text-gray-400 mb-10">All rights reserved</p>
              <div className="flex gap-4">
                <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap underline">LinkedIn</span>
                <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap underline">Twitter</span>
                <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap underline">GitHub</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-16 items-start">
            <div className="col-span-4 rounded-3xl overflow-hidden h-[280px]">
              <img 
                src="https://readdy.ai/api/search-image?query=modern-office-workspace-with-ios-development-team-working-on-mobile-applications-showing-professional-tech-environment-with-purple-tint-overlay-contemporary-software-development-setting-clean-aesthetic&width=420&height=280&seq=office001&orientation=landscape" 
                alt="Office" 
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="col-span-3 flex flex-col justify-center">
              <h3 className="text-5xl text-white font-serif leading-tight">
                Get in touch<br />
                with our team<br />
                of experts
              </h3>
            </div>

            <div className="col-span-5">
              <p className="text-base text-gray-400 leading-relaxed mb-10">
                Stay updated with our latest iOS innovations and product releases
              </p>
              
              <form 
                id="contact-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d67k61fmvg9ih2c7971g"
                method="POST"
                className="mb-20"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const email = formData.get('email') as string;
                  
                  if (!email || !email.includes('@')) {
                    alert('Please enter a valid email address');
                    return;
                  }

                  fetch(form.action, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams(formData as any).toString(),
                  })
                    .then(() => {
                      alert('Thank you for subscribing!');
                      form.reset();
                    })
                    .catch(() => {
                      alert('Something went wrong. Please try again.');
                    });
                }}
              >
                <div className="flex items-center gap-4">
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    required
                    className="flex-1 h-14 px-7 bg-transparent border border-gray-700 rounded-full text-base text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                  <button
                    type="submit"
                    className="h-14 px-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center gap-2 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Subscribe
                    <i className="ri-arrow-right-line text-lg"></i>
                  </button>
                </div>
              </form>

              <div className="grid grid-cols-2 gap-10">
                <div className="space-y-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-map-pin-line text-orange-500 text-base"></i>
                      <span className="text-xs uppercase tracking-widest text-orange-500">Address</span>
                    </div>
                    <p className="text-lg text-white">Frankopanska 80</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-building-line text-orange-500 text-base"></i>
                      <span className="text-xs uppercase tracking-widest text-orange-500">City</span>
                    </div>
                    <p className="text-lg text-white">Pozega</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-mail-line text-orange-500 text-base"></i>
                      <span className="text-xs uppercase tracking-widest text-orange-500">Zip</span>
                    </div>
                    <p className="text-lg text-white">34000</p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-global-line text-orange-500 text-base"></i>
                      <span className="text-xs uppercase tracking-widest text-orange-500">Country</span>
                    </div>
                    <p className="text-lg text-white">Croatia</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-mail-send-line text-orange-500 text-base"></i>
                      <span className="text-xs uppercase tracking-widest text-orange-500">Email</span>
                    </div>
                    <p className="text-lg text-white">info@denacdoo.com</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-phone-line text-orange-500 text-base"></i>
                      <span className="text-xs uppercase tracking-widest text-orange-500">Number</span>
                    </div>
                    <p className="text-lg text-white">+1 7542276388</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-20 pt-10 border-t border-gray-800 text-center">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
